import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../BaileyBits';
import { Row, Section } from '../BaileyHome';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import PhonePanel from './PhonePanel';
import { useNeeds, useNeedActions, NeedsList, NeedsLine } from './NeedsYou';
import AnswerPanel from './AnswerPanel';
import VoicePanel, { VoiceRow } from './VoicePanel';
import { ConversationPanel, ConversationRow } from './ConversationPanels';
import { SoulMark } from './SoulCards';
import { soulSummary, soulHue } from './soul';
import { prettyNumber, errorText, priceLine, toE164 } from './api';
import { answeringLabel } from './glance';
import ReceptionistAllowance from '../../pricing/ReceptionistAllowance';

/**
 * Bailey's front desk. In order: where Bailey answers (the two switches,
 * with the result beside the heading), messages waiting for you, the latest
 * conversations (checked most days), how Bailey talks and sounds, and what
 * Bailey knows (both set once).
 *
 * The switches are the on and off: there is no separate "front desk" switch
 * to fall out of step with them.
 */
function Toggle({ on, disabled, onChange, label }) {
    return (
        <button
            type="button"
            className={`ds-toggle${on ? ' ds-toggle--on' : ''}${disabled ? ' ds-toggle--disabled' : ''}`}
            role="switch"
            aria-checked={on}
            aria-label={label}
            disabled={disabled}
            onClick={() => { hapticTick(); onChange(!on); }}
        >
            <span className="ds-toggle-knob" />
        </button>
    );
}

/** Until the settings are in, nothing on the page can be switched: a switch pressed early would save blanks over the salon's own. */
function Waiting({ fd }) {
    if (fd.error) {
        return (
            <div className="bly-home fd-page">
                <div className="bly-error" role="alert">{fd.error}</div>
                <div className="bly-actions" style={{ justifyContent: 'flex-start' }}>
                    <button type="button" className="bly-btn is-secondary" onClick={() => { hapticTick(); fd.refresh(); }}>Try again</button>
                </div>
            </div>
        );
    }
    return (
        <div className="bly-home fd-page" aria-busy="true" aria-label="Loading your front desk">
            {['Where I answer', 'Conversations', 'How I talk', 'What I know'].map((label, i) => (
                <Section key={label} label={label}>
                    <div className="bly-card fd-wait" style={{ height: i === 0 ? 132 : 66 }} />
                </Section>
            ))}
        </div>
    );
}

export default function FrontDeskPage({ fd, onOpenSoul, onOpenConversations, onOpenMessages, onCall, history, salonName }) {
    const [panel, setPanel] = useState(null);   // 'phone' | 'voice' | { conversation } | { answer } | { add, question }
    const [busy, setBusy] = useState({});      // which switches are saving
    const [error, setError] = useState('');
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);
    const s = fd.settings;
    // Messages waiting, one row per person: three here, the rest on Messages.
    const people = useNeeds(fd);
    const needs = useNeedActions({ fd, groups: people, history, salonName });

    // Saves queue in the hook, each on top of the last, so both switches
    // can be used at once; each is held only while its own save runs.
    const change = async (patch, key) => {
        setBusy((b) => ({ ...b, [key]: true })); setError('');
        try { await fd.save(patch); hapticSuccess(); }
        catch (e) { hapticError(); if (alive.current) setError(errorText(e, 'That didn’t save. Try again.')); }
        if (alive.current) setBusy((b) => ({ ...b, [key]: false }));
    };

    if (!fd.ready) return <Waiting fd={fd} />;

    const transfer = toE164(s.voiceTransferNumber);
    const status = answeringLabel(s, onCall);
    const live = status !== 'Off';

    return (
        <div className="bly-home fd-page">
            {(error || fd.error) && <div className="bly-error" role="alert">{error || fd.error}</div>}

            <Section label="Where I answer" action={<span className={`fd-state${live ? ' is-live' : ''}${onCall ? ' is-call' : ''}`} aria-live="polite">{live && <i />}{status}</span>}>
                <div className="bly-card bly-list">
                    <Row
                        icon="💬"
                        title="Chat on your booking page"
                        sub="Clients ask questions, get advice and book, any time"
                        right={<Toggle on={s.enabled} disabled={!!busy.chat} label="Chat on your booking page" onChange={(v) => change({ enabled: v }, 'chat')} />}
                    />
                    {s.voiceNumber ? (
                        <Row
                            icon="📞"
                            title="Answer your phone"
                            sub={`${prettyNumber(s.voiceNumber)} · ${s.voicePricePerMinute != null ? `${s.voicePricePerMinute}p a minute` : 'Charged per minute'}`}
                            right={<Toggle on={s.voiceEnabled} disabled={!!busy.phone} label="Answer your phone" onChange={(v) => change({ voiceEnabled: v }, 'phone')} />}
                        />
                    ) : (
                        <Row
                            icon="📞"
                            title="Answer your phone"
                            sub="Get your own local number first"
                            right={<button type="button" className="bly-btn is-secondary fd-row-btn" onClick={() => { hapticTick(); setPanel('phone'); }}>Set up</button>}
                        />
                    )}
                    {!!s.voiceNumber && (
                        <Row
                            icon="⚙️"
                            title="Phone settings"
                            sub={transfer ? `Your number, and callers asking for a person go to ${prettyNumber(transfer)}` : 'Your number, and what happens when someone asks for a person'}
                            onClick={() => setPanel('phone')}
                        />
                    )}
                </div>
                {!s.voiceNumber && <p className="fd-section-note">{priceLine(s)}</p>}
            </Section>

            {(!!fd.open.length || !!needs.undoBar) && (
                <Section label="Messages for you" count={fd.open.length}>
                    <NeedsLine groups={people} />
                    {needs.undoBar}
                    <NeedsList fd={fd} groups={people} limit={3} onSeeAll={onOpenMessages} act={needs.act} />
                </Section>
            )}
            {needs.panels}

            {(fd.on || fd.conversations.length > 0) && (
                <Section label="Conversations">
                    <div className="bly-card bly-list">
                        {fd.conversations.slice(0, 5).map((c) => <ConversationRow key={c.id} c={c} onOpen={(x) => setPanel({ conversation: x })} />)}
                        {!fd.conversations.length && <p className="fd-empty-row">When clients chat or call, you can read every word here.</p>}
                        {/* The way to the rest, as under Messages for you and on the home */}
                        {fd.conversations.length > 0 && (
                            <button type="button" className="bly-list-more fd-list-more" onClick={() => { hapticTick(); onOpenConversations(); }}>See all conversations</button>
                        )}
                    </div>
                </Section>
            )}

            {/* Pricing (Oct 2026): this month's minutes and replies, and the way to Plan & usage. */}
            <ReceptionistAllowance history={history} wrap={(card) => <Section label="This month">{card}</Section>} />

            <Section label="How I talk">
                <div className="bly-card bly-list">
                    <Row
                        icon={<span className="fd-soul-tile"><SoulMark hue={soulHue(fd.soul)} /></span>}
                        title={soulSummary(fd.soul)}
                        sub="My soul, for your clients and for you"
                        onClick={onOpenSoul}
                    />
                    <VoiceRow fd={fd} onOpen={() => setPanel('voice')} />
                </div>
            </Section>

            <Section
                label="What I know"
                action={<button type="button" className="bly-btn is-secondary fd-sec-btn" onClick={() => { hapticTick(); setPanel({ add: true, question: '' }); }}><Icon.Plus size={15} />Add</button>}
            >
                <div className="bly-card bly-list">
                    <Row
                        icon={<span className="fd-tick-tile"><Icon.Tick size={15} /></span>}
                        title="Your prices, services and opening hours"
                        sub="Free times and your team too, straight from Beu"
                        right={null}
                    />
                    {fd.answers.map((r) => (
                        <button key={r._id} type="button" className="fd-answer" onClick={() => { hapticTick(); setPanel({ answer: r }); }}>
                            <span className="fd-answer-copy">
                                <b>{r.question}</b>
                                <small>{r.answer}</small>
                            </span>
                            <span className="bly-chev" aria-hidden="true"><Icon.Chevron /></span>
                        </button>
                    ))}
                    {!fd.answers.length && (
                        <button type="button" className="fd-answer is-empty" onClick={() => { hapticTick(); setPanel({ add: true, question: '' }); }}>
                            <span className="fd-answer-copy">
                                <b>Add what Beu can’t know</b>
                                <small>Parking, patch tests, dogs, access. Anything clients ask that isn’t in your account.</small>
                            </span>
                            <span className="bly-chev" aria-hidden="true"><Icon.Chevron /></span>
                        </button>
                    )}
                </div>
            </Section>

            {panel === 'phone' && <PhonePanel fd={fd} onClose={() => setPanel(null)} />}
            {panel === 'voice' && <VoicePanel fd={fd} onClose={() => setPanel(null)} />}
            {panel && panel.conversation && <ConversationPanel conversation={panel.conversation} onClose={() => setPanel(null)} onTeach={() => setPanel({ add: true, question: '' })} />}
            {panel && (panel.answer || panel.add) && (
                <AnswerPanel
                    answer={panel.answer || null}
                    question={panel.question || ''}
                    answers={fd.answers}
                    onAdd={({ question, answer }) => fd.addAnswer(question, answer)}
                    onChange={(old, next) => fd.changeAnswer(old, next)}
                    onRemove={(old) => fd.removeAnswer(old._id)}
                    onClose={() => setPanel(null)}
                />
            )}
        </div>
    );
}
